import React, { useEffect, useState } from "react";
import { OrderFormType } from "../../interfaces/orderFormType";
import {
  determineOrderFormTypes,
  downloadBlankBackgroundCheckAgreementForm,
  downloadBlankCandidateQuestionnaire,
  downloadBlankCreditReportAuthForm,
  downloadBlankCriminalAuthForm,
  downloadBlankIdentificationForm,
  downloadBlankMotorVehicleAuthForm,
  downloadBlankReferenceAuthForm,
  downloadBlankSocialMediaAuthForm,
  downloadMailingWindowOrder,
  downloadPreFillTransferReleaseForm,
  getOrderFormByOrderIDType,
  getOrderFormTypes,
} from "../../services/orderFormService";
import { QuestionOption } from "../../interfaces/questionOption";
import {
  getCandidateQuestionnaireBase,
  getCandidateQuestionnaireQuestionOptions,
  getCandidateQuestionnaireQuestions,
} from "../../services/candidateQuestionnaireService";
import { OrderService } from "../../interfaces/orderService";
import { getOrderServicesByOrderID } from "../../services/orderServiceService";
import { TransferRelease } from "../../pages/candidate-pages/candidate-forms/TransferReleaseForm";
import { getAdditionalForm } from "../../services/additionalFormService";
import { useViewOrderContext } from "../../contexts/ViewOrderContext";

interface DownloadCandidateFormProps {
  onClose: (message?: string) => void;
}

const DownloadCandidateForm: React.FC<DownloadCandidateFormProps> = ({
  onClose,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [availableOrderFormTypes, setAvailableOrderFormTypes] = useState<
    OrderFormType[] | null
  >(null);
  const [selectedFormType, setSelectedFormType] =
    useState<OrderFormType | null>(null);
  const [hasEmploymentService, setHasEmploymentService] = useState(false);
  const [fiveYears, setFiveYears] = useState(false);
  const [hasOneEducationService, setHasOneEducationService] = useState(false);
  const [hasMultipleEducationService, setHasMultipleEducationService] =
    useState(false);
  const [hasMonitoringService, setHasMonitoringService] = useState(false);

  const {
    client,
    order,
    referenceOrder,
    criminalOrder,
    subscriptionOrder,
    orderFormTypes,
  } = useViewOrderContext();

  useEffect(() => {
    const getDetails = async () => {
      try {
        setLoadingSpinner(true);
        const orderFormTypesResponse = await getOrderFormTypes();

        const { forms, isFiveYears } = determineOrderFormTypes(
          client,
          orderFormTypesResponse,
          referenceOrder,
          criminalOrder,
          subscriptionOrder,
        );

        setFiveYears(isFiveYears);
        setAvailableOrderFormTypes(forms);

        const orderServicesResponse = await getOrderServicesByOrderID(
          order.orderID,
        );
        if (orderServicesResponse) {
          setHasEmploymentService(
            orderServicesResponse?.some(
              (orderService: OrderService) =>
                orderService.serviceID === 30 ||
                orderService.serviceID === 31 ||
                orderService.serviceID === 32,
            ),
          );
          const hasOne = orderServicesResponse?.some(
            (orderService: OrderService) => orderService.serviceID === 9,
          );
          const hasMultiple = orderServicesResponse?.some(
            (orderService: OrderService) => orderService.serviceID === 23,
          );
          setHasOneEducationService(hasOne);
          setHasMultipleEducationService(hasMultiple);

          const monitoring = orderServicesResponse.some(
            (orderService: OrderService) => orderService.serviceID === 36,
          );
          setHasMonitoringService(monitoring);
        }
      } catch (error) {
        console.error("Error getting order form types:", error);
      } finally {
        setLoadingSpinner(false);
      }
    };

    getDetails();
  }, [
    referenceOrder,
    criminalOrder,
    client?.denomination,
    order?.orderID,
    orderFormTypes,
    client,
    subscriptionOrder,
  ]);

  const handleDownloadForm = async () => {
    if (!selectedFormType || !client) return;

    try {
      setLoadingSpinner(true);
      let response = null;

      switch (selectedFormType.orderFormTypeID) {
        case 0:
          response = await downloadMailingWindowOrder(order);
          break;
        case 1:
          response = await downloadBlankBackgroundCheckAgreementForm(
            client?.diocese ? client.diocese : "",
          );
          break;
        case 2:
          response = await downloadBlankCriminalAuthForm(
            criminalOrder?.years ? criminalOrder.years : 0,
            client.diocese ? client.diocese : "",
            client.abbreviation ? client.abbreviation : "",
            hasEmploymentService,
            hasOneEducationService || hasMultipleEducationService
              ? true
              : false,
            hasOneEducationService,
            hasMonitoringService,
          );
          break;
        case 3:
          response = await downloadBlankCreditReportAuthForm(
            client.primaryFirstName,
            client.primaryLastName,
            client.primaryTitle,
            client.diocese ? client.diocese : "",
            client.abbreviation ? client.abbreviation : "",
            client.clientName,
          );
          break;
        case 4:
          response = await downloadBlankMotorVehicleAuthForm(
            client.primaryFirstName,
            client.primaryLastName,
            client.primaryTitle,
            client.diocese ? client.diocese : "",
            client.clientName,
          );
          break;
        case 5:
          if (referenceOrder) {
            const logoForm = await getAdditionalForm(client.clientID, 5);
            response = await downloadBlankReferenceAuthForm(
              client.clientID,
              client.diocese ? client.diocese : "",
              client.diocese2 ? client.diocese2 : "",
              client.clientName ? client.clientName : "",
              referenceOrder,
              logoForm,
            );
          }
          break;
        case 6:
          const candidateQuestionnaire = await getCandidateQuestionnaireBase(
            client.clientID,
            fiveYears,
          );

          const questions = await getCandidateQuestionnaireQuestions(
            candidateQuestionnaire.candidateQuestionnaireID,
          );

          const questionsWithOptionsMap = new Map<
            number,
            { questionText: string; options: QuestionOption[] }
          >();

          const initialResponses: Record<string, string> = {};

          for (const question of questions) {
            const options = await getCandidateQuestionnaireQuestionOptions(
              question.questionID,
            );
            questionsWithOptionsMap.set(question.questionID, {
              questionText: question.questionText,
              options: options,
            });

            if (options.length > 0) {
              options.forEach((option) => {
                initialResponses[`${option.questionOptionID}`] = "";
              });
            } else {
              initialResponses[`${question.questionID}`] = "";
            }
          }

          const questionsWithOptions = questionsWithOptionsMap;

          const orderForm = await getOrderFormByOrderIDType(order.orderID, 6);

          const logoForm = await getAdditionalForm(client.clientID, 5);

          response = await downloadBlankCandidateQuestionnaire(
            client.clientID,
            orderForm?.orderFormID ? orderForm?.orderFormID : 0,
            candidateQuestionnaire,
            questionsWithOptions,
            client.diocese ? client.diocese : "",
            client.diocese2 ? client.diocese2 : "",
            logoForm,
          );
          break;
        case 7:
          if (referenceOrder) {
            response = await downloadBlankIdentificationForm(
              referenceOrder,
              client.diocese ? client.diocese : "",
              client.clientID,
            );
          }
          break;
        case 8:
          if (referenceOrder) {
            const transferRelease: TransferRelease = {
              re:
                order.candidateFirstName +
                " " +
                order.candidateMI +
                " " +
                order.candidateLastName,
              ssn: order.candidateSSN ? order.candidateSSN : "",
              requestingEntityName: client.diocese
                ? client.diocese
                : client.clientName,
              requestingEntityTypedName: "",
              requestingEntitySignature: "",
              requestingEntityAuthorizedDate: new Date(),
              requestingEntityAddress: "",
              clergyWitnessName: "",
              clergyTypedName: "",
              clergySignature: "",
              clergyWitnessSignature: "",
              clergyDate: new Date(),
              clergySSN: "",
              authorizingEntityName: referenceOrder.priorClientName,
              authorizingEntitySignature: "",
              authorizingEntityDate: new Date(),
              authorizingEntityAddress: "",
              authorizingEntityTypedName: "",
            };
            response = await downloadPreFillTransferReleaseForm(
              order,
              transferRelease,
            );
          }
          break;
        case 9:
          response = await downloadBlankSocialMediaAuthForm(
            client.primaryFirstName,
            client.primaryLastName,
            client.primaryTitle,
            client.diocese ? client.diocese : "",
            client.abbreviation ? client.abbreviation : "",
            client.clientName,
          );
          break;
        default:
          throw new Error("Invalid form type selected");
      }

      if (!response) throw new Error("No response from server");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fileName = selectedFormType?.description
        ? `${selectedFormType.description.replace(/\s+/g, "_")}.pdf`
        : "Downloaded_Form.pdf";
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } catch (error) {
      console.error("Error handling form download:", error);
      setErrorMessage("Error downloading candidate form.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <>
      <div className="background-mask" onClick={() => onClose()}></div>
      <div className="filter-popup">
        <div className="close-button-container">
          <button className="close-button" onClick={() => onClose()}>
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="filter-header">
          <h2>Download Blank Candidate Form</h2>
          <div>Please choose a form type.</div>
        </div>
        <br></br>
        <div className="dialog-container">
          <div className="form-group">
            <select
              id="formTypeSelect"
              className="form-control"
              value={selectedFormType?.description || ""}
              onChange={(e) => {
                const selected = availableOrderFormTypes?.find(
                  (type) => type.description === e.target.value,
                );
                setSelectedFormType(selected || null);
              }}
            >
              <option value="" disabled>
                -- Select Form Type --
              </option>
              {availableOrderFormTypes?.map((formType) => (
                <option
                  key={formType.orderFormTypeID}
                  value={formType.description}
                >
                  {formType.description}
                </option>
              ))}
            </select>
          </div>
          <div className="action-buttons">
            <button
              className="smbtn btn-grey"
              onClick={() => handleDownloadForm()}
            >
              Download Candidate Form
            </button>
          </div>
          {errorMessage && <div className="error-message">{errorMessage}</div>}
        </div>
        {errorMessage && <div className="error-message">{errorMessage}</div>}
      </div>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </>
  );
};

export default DownloadCandidateForm;
